import React from 'react';
import { HTMLReactParserOptions } from 'html-react-parser';
import { isMathMlTag } from '../../../../../../utils/mathml';
import {
  VignetteAnswerOption,
  VignetteContent,
} from '../../../../../../types/Vignette';
import { getAnswerOptionLetter } from '../../../../../../utils/vignetteAnswerOption';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui/modal';
import moment from 'moment';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { parseHtmlWithMathLatex } from '../../../../../../utils/htmlParser';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface ScheduledQuestionPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  content?: VignetteContent;
  dueAt?: string | null;
  /**
   * Study this variant now, where the modal was opened from (ORA-831). Optional:
   * a surface that can only preview the variant — the exam screen — passes
   * nothing and keeps the single Close it always had.
   */
  onStudyNow?: () => void;
  /** The study request is in flight; the button says so and refuses a second. */
  isStudyNowPending?: boolean;
  /**
   * Why the study request was refused, when the caller knows. Shown in the
   * footer rather than behind the modal, which is where the student is looking.
   */
  studyNowError?: string | null;
}

const parserOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (
      domNode.type === 'tag' &&
      'name' in domNode &&
      isMathMlTag(domNode.name)
    ) {
      return undefined;
    }
    return undefined;
  },
};

const sortAnswerOptionsByLetter = (answerOptions: VignetteAnswerOption[]) =>
  [...answerOptions].sort((left, right) =>
    getAnswerOptionLetter(left).localeCompare(getAnswerOptionLetter(right))
  );

const ScheduledQuestionPreviewModal: React.FC<
  ScheduledQuestionPreviewModalProps
> = ({
  isOpen,
  onClose,
  content,
  dueAt,
  onStudyNow,
  isStudyNowPending = false,
  studyNowError,
}) => {
  if (!content) {
    return null;
  }

  const sortedAnswerOptions = sortAnswerOptionsByLetter(content.answerOptions);

  const scheduledDateText =
    dueAt && moment(dueAt).isValid()
      ? `Scheduled for ${moment(dueAt).format('MMMM D, YYYY')}`
      : 'Scheduled date unavailable';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      name="scheduled-qbank-preview"
      className="modal--glass"
    >
      <ModalHeader className="modal_header">
        <h2>Scheduled QBank Variant</h2>
        <div className="flex items-center gap-2">
          <ClockCounterClockwise />
          {scheduledDateText}
        </div>
      </ModalHeader>
      <ModalBody className="modal_content">
        {/* A preview of a question the student has not answered yet, so its
            keyword explainers stay inert here for the same reason they do
            pre-answer in QBank and chat: the popover can give the answer away. */}
        <div
          className="scheduled-question"
          data-explainer-link-disabled="true"
          data-explainers-disabled="true"
        >
          {parseHtmlWithMathLatex(content.question, parserOptions)}
        </div>
        <div
          className="scheduled-options"
          data-explainer-link-disabled="true"
          data-explainers-disabled="true"
        >
          {sortedAnswerOptions.map((option, index) => {
            const optionLetter = getAnswerOptionLetter(option, index);
            return (
              <div
                key={`${content.id}-scheduled-preview-${optionLetter}-${index}`}
                className="scheduled-options_option"
              >
                <span className="scheduled-options_option_letter">
                  {optionLetter}.
                </span>
                <span className="scheduled-options_option_text">
                  {parseHtmlWithMathLatex(option.optionText, parserOptions)}
                </span>
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter className="modal_actions">
        {/* A refusal outlives the offer it refused — the content behind the
            badge can be gone for good — so the line keeps the two-slot layout
            even once "Study Now" has withdrawn itself. */}
        {onStudyNow || studyNowError ? (
          <div className="flex w-full flex-col gap-2">
            {studyNowError ? (
              <p className="!m-0 text-xs text-status-danger" role="alert">
                {studyNowError}
              </p>
            ) : null}
            {/* Stacked on a phone and reversed, so the primary action is the one
                under the student's thumb — the same order the leave modal
                uses. */}
            <div className="flex flex-col-reverse gap-2 mobile:flex-row mobile:justify-end">
              <Button
                className="min-h-11 w-full mobile:w-auto"
                context="modal"
                onClick={onClose}
                type="button"
                variant="outline"
              >
                Close
              </Button>
              {onStudyNow ? (
                <Button
                  aria-busy={isStudyNowPending || undefined}
                  className="min-h-11 w-full mobile:w-auto"
                  disabled={isStudyNowPending}
                  onClick={onStudyNow}
                  type="button"
                >
                  {isStudyNowPending ? (
                    <Spinner size={14} thickness={3} aria-hidden="true" />
                  ) : null}
                  Study Now
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          // Preview-only, as the exam screen has always had it: one Close, and
          // the footer's own only-child rule is what right-aligns it.
          <Button onClick={onClose} context="modal" variant="outline">
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ScheduledQuestionPreviewModal;
